import React, { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCcw, Loader2, ShieldCheck, ShieldX, Clock } from 'lucide-react';
import api from '../../api/client';
import { fmtCredits } from '../../lib/currency';

/**
 * Phase 90 — Admin panel for Stripe disputes (chargebacks).
 * Shows lifecycle (open / won / lost), disputed amount, frozen Credits,
 * spent-attribution and the creator quotas reserved for each dispute.
 */
const LIFECYCLE_BADGE = {
  open: { label: 'Aperta', cls: 'bg-amber-500/20 gold-text', Icon: Clock },
  won: { label: 'Vinta', cls: 'bg-emerald-500/20 text-emerald-300', Icon: ShieldCheck },
  lost: { label: 'Persa', cls: 'bg-red-500/20 text-red-300', Icon: ShieldX },
};

const AdminDisputesPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/disputes');
      setRows(res.data || []);
    } catch (_e) { /* soft */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="admin-disputes-panel">
      <header className="flex items-center justify-between border-b border-border px-5 py-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 gold-text" />
          <h2 className="text-sm font-semibold">Dispute Stripe (chargeback)</h2>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-secondary"
          data-testid="admin-disputes-refresh"
        >
          <RefreshCcw className="h-3 w-3" /> Aggiorna
        </button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
        </div>
      ) : rows.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-muted-foreground" data-testid="admin-disputes-empty">
          Nessuna dispute registrata. Le contestazioni Stripe appariranno qui automaticamente.
        </p>
      ) : (
        <div className="divide-y divide-border">
          {rows.map((d) => {
            const badge = LIFECYCLE_BADGE[d.lifecycle] || LIFECYCLE_BADGE.open;
            const Icon = badge.Icon;
            return (
              <div key={d.stripe_dispute_id} className="px-5 py-4" data-testid={`dispute-row-${d.stripe_dispute_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${badge.cls}`}>
                    <Icon className="h-3 w-3" /> {badge.label}
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    €{((d.amount_cents || 0) / 100).toFixed(2)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {d.username ? `@${d.username}` : '—'} {d.user_email ? `(${d.user_email})` : ''}
                  </span>
                  <span className="ml-auto text-[11px] text-muted-foreground">
                    {d.created_at ? new Date(d.created_at).toLocaleString('it-IT') : ''}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[12px] sm:grid-cols-4">
                  <Cell label="Credits contestati" value={fmtCredits(d.credits_disputed || 0)} />
                  <Cell label="Congelati (wallet)" value={fmtCredits(d.credits_frozen || 0)} />
                  <Cell label="Già spesi attribuiti" value={fmtCredits(d.credits_spent_attributed || 0)} />
                  <Cell
                    label={d.lifecycle === 'lost' ? 'Perdita non recuperata' : 'Non coperto'}
                    value={fmtCredits((d.lifecycle === 'lost' ? d.unrecovered_credits : d.uncovered_credits) || 0)}
                    warn={((d.lifecycle === 'lost' ? d.unrecovered_credits : d.uncovered_credits) || 0) > 0}
                  />
                </div>
                {(d.seller_reservations || []).length > 0 && (
                  <div className="mt-2 rounded-md border border-border bg-background px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Voci Pro coinvolte</p>
                    <ul className="mt-1 space-y-0.5 text-[12px]">
                      {d.seller_reservations.map((r, i) => (
                        <li key={i} className="flex items-center justify-between gap-2">
                          <span>@{r.seller_username || r.seller_id?.slice(0, 8)} · {r.item_type}</span>
                          <span className="tabular-nums text-muted-foreground">
                            riservati {fmtCredits((r.frozen_from_balance || 0) + (r.frozen_from_pending || 0))} / {fmtCredits(r.requested_net || 0)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  Ricarica: <code className="rounded bg-muted px-1 py-0.5">{d.purchase_id || 'non ricondotta'}</code>
                  {' · '}Dispute: <code className="rounded bg-muted px-1 py-0.5">{d.stripe_dispute_id}</code>
                  {d.reason ? ` · Motivo: ${d.reason}` : ''}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Cell = ({ label, value, warn }) => (
  <div className="rounded-md border border-border bg-background px-2.5 py-1.5">
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`text-sm font-semibold tabular-nums ${warn ? 'text-red-300' : 'text-foreground'}`}>{value}</p>
  </div>
);

export default AdminDisputesPanel;
